import { Direction, StateMachine, asyncTween } from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyState } from 'tower/scenes/battle/enemy';
import { MazeNode } from 'tower/scenes/battle/maze';
import Room from '../room';

const PUSH_DELAY_MS = 250;

interface Variant {
  idle: number;
}

const VARIANTS: Record<string, Variant> = {
  horizontal: {
    idle: 7,
  },
};

export default class MrWall extends Enemy {
  /** Node to the left of where Mr. Wall sits. */
  mazeNode: MazeNode;

  constructor(
    scene: BattleScene,
    room: Room,
    x: number,
    y: number,
    public variantType = 'horizontal',
  ) {
    // NOTE: The X and Y given are for the space _above_ where Mr.Wall hangs out

    const variant = VARIANTS[variantType];
    super(scene, x, y + 12, 'enemies', variant.idle, 'mrWall', { hideSpawn: true });
    this.body.setSize(16, 10);

    this.mazeNode = room.maze.getNodesAtWorldXY(x, y)[2];
  }

  makeStateMachine(scene: BattleScene): StateMachine<EnemyState> {
    return new StateMachine('idle', {
      idle: new MrWallIdleState(scene, this),
      pushing: new MrWallPushingState(scene, this),
      moving: new MrWallMovingState(scene, this),
    });
  }

  get variant() {
    return VARIANTS[this.variantType];
  }
}

class MrWallState extends EnemyState {
  declare enemy: MrWall;
}

class MrWallIdleState extends MrWallState {
  async handleEntered() {
    const { enemy } = this;

    enemy.stop().setFrame(enemy.variant.idle);
  }

  update() {
    const { scene, enemy } = this;
    const { player } = scene;

    if (scene.controller.up.isDown && scene.physics.overlap(player, enemy)) {
      return this.transition('pushing');
    }
  }
}

class MrWallPushingState extends MrWallState {
  startTime = 0;
  missCount = 0;

  handleEntered() {
    this.startTime = this.scene.time.now;
    this.missCount = 0;
  }

  update() {
    const { scene, enemy } = this;
    const { player } = scene;

    if (scene.time.now > this.startTime + PUSH_DELAY_MS) {
      this.transition('moving');
    }

    if (!(scene.controller.up.isDown && scene.physics.overlap(player, enemy))) {
      this.missCount++;
      if (this.missCount > 4) {
        return this.transition('idle');
      }
    } else {
      this.missCount = 0;
    }
  }
}

class MrWallMovingState extends MrWallState {
  async handleEntered() {
    const { scene, enemy } = this;

    scene.currentRoom.maze.removeWall(enemy.mazeNode, Direction.Right);
    scene.sound.play('secret');

    await asyncTween(scene, {
      y: enemy.y - 24,
      targets: [enemy],
      duration: 500,
    });

    scene.events.emit('enemydeath', enemy);
    enemy.destroy();
  }
}
